import { type ALHubspotSupport, useToast } from '@af-lib/vue-ui';
import axios from 'axios';
import mixpanel from 'mixpanel-browser';
import { computed, useTemplateRef } from 'vue';

import { getConfig } from '@/config/fetchEnv';
import { i18next, t } from '@/config/i18n';
import { useUserStore } from '@/stores/user';

export function useHubspotSupport() {
  const supportRef = useTemplateRef<InstanceType<typeof ALHubspotSupport>>('supportRef');
  const config = getConfig();
  const locale = computed(() => i18next.language ?? 'en-US');
  const userStore = useUserStore();
  const toast = useToast();

  const supportData = computed(() => ({
    uuid: '',
    email: '',
    companyUuid: '',
    env: config.environment,
    locale: locale.value
  }));

  async function onSubmit(formData: FormData) {
    formData.append('url', window.location.href);

    try {
      const { data } = await axios.post('/api/support/ticket', formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Le DS n'expose aucune « fin de chargement » : son bouton reste en
      // isLoading tant que le popover n'est pas refermé. Même contrat que
      // assessments (SupportButton.vue) — toast avec l'id du ticket, puis close().
      toast.success(t('general.support.widget.ai_chatbot.success_ticket', { ticket: data?.id ?? '' }));
      supportRef.value?.close();

      try {
        const distinctId = userStore.user?.uuid ?? mixpanel.get_distinct_id();
        if (distinctId) {
          mixpanel.track('support_form-submit', {
            distinct_id: distinctId,
            slug: window.location.pathname,
            user_uuid: userStore.user?.uuid ?? null,
            email: userStore.user?.email ?? (formData.get('email') as string | null),
            language: locale.value,
            ticket_category: formData.get('category') as string | null
          });
        }
      } catch {
        /* non-blocking */
      }
    } catch {
      // Pas d'addError() ici : il écrit dans le fil de discussion, or l'envoi se
      // fait depuis la vue formulaire, où le composant de chat est démonté — la
      // méthode ne ferait rien. Le toast est le seul retour possible, et close()
      // est ce qui remet le bouton du DS hors de son état de chargement.
      toast.error(t('general.error.general'));
      supportRef.value?.close();
    }
  }

  async function onSendChatMessage(message: string) {
    const chatUrl = config.chatbot_support_url;
    const chatKey = config.chatbot_support_key;
    if (!chatUrl || !chatKey) return;

    try {
      const res = await fetch(`${chatUrl}chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': chatKey
        },
        body: JSON.stringify({ question: message, session_id: null })
      });
      const data = await res.json();
      supportRef.value?.addChatMessage(data.answer);
    } catch {
      supportRef.value?.addError();
    }
  }

  return { supportData, onSubmit, onSendChatMessage };
}
